import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { 
  RefreshCw, 
  Download, 
  Filter,
  X,
  CalendarIcon
} from 'lucide-react';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { useDiagnostics } from './DiagnosticsContext';
import { DIAGNOSTICS_START_DATE } from './DiagnosticsTypes';

const DiagnosticsFilters: React.FC = () => {
  const { 
    filters, 
    setFilters, 
    refreshData, 
    exportData, 
    isLoading,
    data 
  } = useDiagnostics();

  const severityOptions = [
    { value: 'critical', label: 'Критическая', color: 'red' },
    { value: 'high', label: 'Высокая', color: 'red' },
    { value: 'medium', label: 'Средняя', color: 'yellow' },
    { value: 'low', label: 'Низкая', color: 'blue' }
  ];

  const rentalStatusOptions = [
    { value: 'active', label: 'Активная' },
    { value: 'completed', label: 'Завершенная' },
    { value: 'cancelled', label: 'Отмененная' }
  ];

  const typeOptions = Array.from(new Set(data.map(item => item.type))).sort();

  const toggleSeverity = (severity: string) => {
    const newSelected = filters.selectedSeverity.includes(severity)
      ? filters.selectedSeverity.filter(s => s !== severity)
      : [...filters.selectedSeverity, severity];
    setFilters({ selectedSeverity: newSelected });
  };

  const toggleType = (type: string) => {
    const newSelected = filters.selectedType.includes(type)
      ? filters.selectedType.filter(t => t !== type)
      : [...filters.selectedType, type];
    setFilters({ selectedType: newSelected });
  };

  const toggleRentalStatus = (status: string) => {
    const newSelected = filters.selectedRentalStatus.includes(status)
      ? filters.selectedRentalStatus.filter(s => s !== status)
      : [...filters.selectedRentalStatus, status];
    setFilters({ selectedRentalStatus: newSelected });
  };

  const clearFilters = () => {
    setFilters({
      selectedSeverity: [],
      selectedType: [],
      searchTerm: '',
      showOnlyIssues: false,
      useCustomDateRange: false,
      dateFrom: new Date(DIAGNOSTICS_START_DATE),
      dateTo: new Date(),
      selectedRentalStatus: [],
      ignoreOldRentals: false,
      ignoreCancelledRentals: false
    });
  };

  const setQuickDateRange = (days: number) => {
    const dateTo = new Date();
    const dateFrom = new Date();
    dateFrom.setDate(dateTo.getDate() - days);
    setFilters({
      useCustomDateRange: true,
      dateFrom,
      dateTo
    });
  };

  const hasActiveFilters = 
    filters.selectedSeverity.length > 0 || 
    filters.selectedType.length > 0 || 
    filters.searchTerm.length > 0 || 
    filters.showOnlyIssues ||
    filters.useCustomDateRange ||
    filters.selectedRentalStatus.length > 0 ||
    filters.ignoreOldRentals ||
    filters.ignoreCancelledRentals;

  // Подсчет результатов по каждому фильтру
  const getFilterCount = (severityFilter?: string, typeFilter?: string) => {
    return data.filter(item => {
      if (severityFilter && item.severity !== severityFilter) return false;
      if (typeFilter && item.type !== typeFilter) return false;
      return true;
    }).length;
  };

  return (
    <Card className="mb-6">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Фильтры и управление
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button 
              onClick={refreshData} 
              disabled={isLoading}
              size="sm"
              variant="outline"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Обновить
            </Button>
            <Button 
              onClick={exportData}
              size="sm"
              variant="outline"
            >
              <Download className="h-4 w-4 mr-2" />
              Экспорт
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Date Range Selection */}
        <div className="space-y-3">
          <div className="flex items-center space-x-2">
            <Switch
              id="custom-date-range"
              checked={filters.useCustomDateRange}
              onCheckedChange={(checked) => setFilters({ useCustomDateRange: checked })}
            />
            <Label htmlFor="custom-date-range">Использовать пользовательский диапазон дат</Label>
          </div>

          {!filters.useCustomDateRange && (
            <div className="text-sm text-muted-foreground">
              Диагностика с {new Date(DIAGNOSTICS_START_DATE).toLocaleDateString('ru-RU')} по текущую дату
            </div>
          )}

          {filters.useCustomDateRange && (
            <div className="space-y-3">
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setQuickDateRange(7)}
                >
                  Последние 7 дней
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setQuickDateRange(30)}
                >
                  Последние 30 дней
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setQuickDateRange(90)}
                >
                  Последние 3 месяца
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setFilters({
                    useCustomDateRange: true,
                    dateFrom: new Date(DIAGNOSTICS_START_DATE),
                    dateTo: new Date()
                  })}
                >
                  Все время
                </Button>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <Label className="text-sm font-medium mb-2 block">Дата начала</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !filters.dateFrom && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {filters.dateFrom ? format(filters.dateFrom, "dd.MM.yyyy", { locale: ru }) : "Выберите дату"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={filters.dateFrom}
                        onSelect={(date) => date && setFilters({ dateFrom: date })}
                        disabled={(date) => date > new Date() || date < new Date(DIAGNOSTICS_START_DATE)}
                        initialFocus
                        className="pointer-events-auto"
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="flex-1">
                  <Label className="text-sm font-medium mb-2 block">Дата окончания</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !filters.dateTo && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {filters.dateTo ? format(filters.dateTo, "dd.MM.yyyy", { locale: ru }) : "Выберите дату"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={filters.dateTo}
                        onSelect={(date) => date && setFilters({ dateTo: date })}
                        disabled={(date) => date > new Date() || date < filters.dateFrom}
                        initialFocus
                        className="pointer-events-auto"
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Search */}
        <div>
          <Label htmlFor="search">Поиск по описанию</Label>
          <Input
            id="search"
            placeholder="Введите текст для поиска..."
            value={filters.searchTerm}
            onChange={(e) => setFilters({ searchTerm: e.target.value })}
          />
        </div>

        {/* Show only critical issues toggle */}
        <div className="flex items-center space-x-2">
          <Switch
            id="show-issues"
            checked={filters.showOnlyIssues}
            onCheckedChange={(checked) => setFilters({ showOnlyIssues: checked })}
          />
          <Label htmlFor="show-issues">Показать только критические проблемы</Label>
        </div>

        {/* Rental-specific filters */}
        <div className="space-y-3">
          <Label className="text-sm font-medium">Дополнительные фильтры для аренд</Label>
          
          <div className="flex items-center space-x-2">
            <Switch
              id="ignore-old-rentals"
              checked={filters.ignoreOldRentals}
              onCheckedChange={(checked) => setFilters({ ignoreOldRentals: checked })}
            />
            <Label htmlFor="ignore-old-rentals">Игнорировать старые аренды (начавшиеся до выбранного периода)</Label>
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="ignore-cancelled-rentals"
              checked={filters.ignoreCancelledRentals}
              onCheckedChange={(checked) => setFilters({ ignoreCancelledRentals: checked })}
            />
            <Label htmlFor="ignore-cancelled-rentals">Игнорировать отмененные аренды</Label>
          </div>

          {/* Rental status filters */}
          <div>
            <Label className="text-sm font-medium mb-2 block">Статус аренды</Label>
            <div className="flex flex-wrap gap-2">
              {rentalStatusOptions.map(option => {
                const isSelected = filters.selectedRentalStatus.includes(option.value);
                return (
                  <Badge
                    key={option.value}
                    variant={isSelected ? 'default' : 'outline'}
                    className="cursor-pointer hover:bg-gray-100 text-sm"
                    onClick={() => toggleRentalStatus(option.value)}
                  >
                    {option.label}
                  </Badge>
                );
              })}
            </div>
          </div>
        </div>

        {/* Severity filters */}
        <div>
          <Label className="text-sm font-medium mb-2 block">
            Уровень серьезности ({data.length} всего)
          </Label>
          <div className="flex flex-wrap gap-2">
            {severityOptions.map(option => {
              const count = getFilterCount(option.value);
              const isSelected = filters.selectedSeverity.includes(option.value);
              return (
                <Badge
                  key={option.value}
                  variant={isSelected ? 'default' : 'outline'}
                  className="cursor-pointer hover:bg-gray-100 text-sm"
                  onClick={() => toggleSeverity(option.value)}
                >
                  {option.label} ({count})
                </Badge>
              );
            })}
          </div>
        </div>

        {/* Type filters */}
        {typeOptions.length > 0 && (
          <div>
            <Label className="text-sm font-medium mb-2 block">
              Тип проблемы
            </Label>
            <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
              {typeOptions.map((type: string) => {
                const count = getFilterCount(undefined, type);
                const isSelected = filters.selectedType.includes(type);
                return (
                  <Badge
                    key={type}
                    variant={isSelected ? 'default' : 'outline'}
                    className="cursor-pointer hover:bg-gray-100 text-xs"
                    onClick={() => toggleType(type)}
                  >
                    {type} ({count})
                  </Badge>
                );
              })}
            </div>
          </div>
        )}

        {/* Active filters summary */}
        {hasActiveFilters && (
          <div className="bg-card/90 border border-border/50 p-4 rounded-lg shadow-sm">
            <div className="flex items-center justify-between">
              <div className="text-sm text-foreground">
                <strong className="text-foreground">Активные фильтры:</strong>
                {filters.useCustomDateRange && (
                  <span className="ml-2 text-foreground">
                    Период: {format(filters.dateFrom, "dd.MM.yyyy", { locale: ru })} - {format(filters.dateTo, "dd.MM.yyyy", { locale: ru })}
                  </span>
                )}
                {filters.selectedSeverity.length > 0 && (
                  <span className="ml-2 text-foreground">Серьезность: {filters.selectedSeverity.join(', ')}</span>
                )}
                {filters.selectedType.length > 0 && (
                  <span className="ml-2 text-foreground">Типы: {filters.selectedType.length} выбрано</span>
                )}
                {filters.searchTerm && (
                  <span className="ml-2 text-foreground">Поиск: "{filters.searchTerm}"</span>
                )}
                {filters.showOnlyIssues && (
                  <span className="ml-2 text-foreground">Только критические</span>
                )}
                {filters.selectedRentalStatus.length > 0 && (
                  <span className="ml-2 text-foreground">Статус аренды: {filters.selectedRentalStatus.join(', ')}</span>
                )}
                {filters.ignoreOldRentals && (
                  <span className="ml-2 text-foreground">Игнорируются старые аренды</span>
                )}
                {filters.ignoreCancelledRentals && (
                  <span className="ml-2 text-foreground">Игнорируются отмененные аренды</span>
                )}
              </div>
              <Button 
                onClick={clearFilters}
                size="sm"
                variant="ghost"
                className="text-foreground hover:text-foreground"
              >
                <X className="h-4 w-4 mr-1" />
                Очистить
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DiagnosticsFilters;
